<template>
  <v-container>
    Branch: {{ branch.name }}
    <v-icon icon="mdi-map-marker" @click="openModal"></v-icon>
    <v-tooltip activator="parent" location="end">Branch</v-tooltip>
    <v-dialog v-model="modalOpen" max-width="700px">
      <v-card>
        <v-card-title>Update Branch</v-card-title>
        <v-card-text></v-card-text>
        <v-data-table-server
          v-model="possibleBranches.selected"
          select-strategy="single"
          show-select
          :items="possibleBranches.data"
          :headers="possibleBranches.headers"
          :items-per-page="possibleBranches.pageOptions.limit"
          item-key="id"
          :items-length="possibleBranches.count"
          :search="possibleBranches.pageOptions.q"
          @update:options="handleLimitChange">
          <template v-slot:top>
            <v-text-field
              v-model="possibleBranches.pageOptions.q"
              class="ma-2"
              density="compact"
              placeholder="Search branch..."
              hide-details></v-text-field>
          </template>
        </v-data-table-server>
        <v-card-actions>
          <v-btn color="secondary" @click="modalOpen = false">Cancel</v-btn>
          <v-btn color="primary" @click="changeBranch">Update</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script setup lang="ts">
import { BranchDTO, IPaginationDTO, PaginationDTO } from '@/dtos';
import { useUpdateStatusStore } from '@/stores';
import { useBranchStore, useBranchYearStore } from '@/stores/modules/admin';
import { SNACKSTATUS } from '@/stores/util';
import { computed, ref, watch } from 'vue';
import type { VDataTableServer } from 'vuetify/components';

interface Props {
  yearId: string;
  teamId: string;
  branchId: string;
}
const props = defineProps<Props>();
const emit = defineEmits(['snack']);
const yearId = computed(() => props.yearId);
const teamId = computed(() => props.teamId);
const branchId = computed(() => props.branchId);

const statusStore = useUpdateStatusStore();
const branchStore = useBranchStore();
const branchYearStore = useBranchYearStore();
const { data: branchData } = branchStore.getBranchByIdQuery(branchId);
const branch = computed(() => branchData.value ?? new BranchDTO());

const pageOptionsPossibleBranchYears = ref<IPaginationDTO>(new PaginationDTO());
const { data: possibleBranchYearData, isLoading: isLoadingPossibleBranchYears } =
  branchYearStore.getBranchesForYearQuery(yearId, pageOptionsPossibleBranchYears);

const possibleBranches = ref({
  pageOptions: new PaginationDTO() as IPaginationDTO,
  count: computed(() => possibleBranchYearData.value?.count ?? 0),
  data: computed(() => possibleBranchYearData.value?.data),
  loading: computed(() => isLoadingPossibleBranchYears),
  selected: [] as string[],
  headers: [{ title: 'Branch', key: 'name', align: 'start', sortable: true }] as const,
});
const modalOpen = ref<boolean>(false);

const changeBranch = async () => {
  branchStore.changeBranchForTeam.mutateAsync({
    teamId: teamId.value,
    yearId: yearId.value,
    branchId: possibleBranches.value.selected[0],
  });
};

const key = computed(() => `branch-change-${teamId.value}-${yearId.value}-${branchId.value}`);

watch(
  () => statusStore.getStatus(key.value),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    modalOpen.value = false;
    emit('snack', 'Team Branch', value, statusStore.getError(key.value));
  },
);

const handleLimitChange = async (value: VDataTableServer) => {
  pageOptionsPossibleBranchYears.value.buildPagination(value);
};

const openModal = () => {
  possibleBranches.value.selected[0] = branchId.value;
  modalOpen.value = true;
};
</script>
